import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/lib/axios';
import { queryKeys } from '@/lib/queryKeys';
import type {
  ApiResponse,
  OrganizationSettings,
  PrivacyContent,
  PrivacyAction,
  DataPractice,
  AboutUs,
  Preference,
  TabConfig,
  DsrTypeOption,
  DsrByEmailResponse,
  Country,
  State,
  CountryCallingInfo,
  InaccuracyMapping,
} from '@/types/api';

// Organization Settings
export const useOrganizationSettings = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.organizationSettings,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<OrganizationSettings>>(
        '/api/privacycenter/getorganizationsettings'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch organization settings');
      }
      return data.result;
    },
  });
};

// Privacy Content
export const usePrivacyContent = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.privacyContent,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<PrivacyContent>>(
        '/api/privacycenter/privacycontent'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch privacy content');
      }
      return data.result;
    },
  });
};

// About Us
export const useAboutUs = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.aboutUs,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<AboutUs>>(
        '/api/privacycenter/aboutus'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch about us');
      }
      return data.result;
    },
  });
};

// Data Practice
export const useDataPractice = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.dataPractice,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<DataPractice>>(
        '/api/privacycenter/datapractice'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch data practice');
      }
      return data.result;
    },
  });
};

// Privacy Actions - Returns single object, not array
export const usePrivacyActions = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.privacyActions,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<PrivacyAction>>(
        '/api/privacycenter/privacyaction'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch privacy actions');
      }
      return data.result;
    },
  });
};

// Preferences
export const usePreferences = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.preferences,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<Preference[]>>(
        '/api/privacycenter/preferences'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch preferences');
      }
      return data.result;
    },
  });
};

// Tab Configuration
export const useTabConfiguration = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.tabConfiguration,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<TabConfig[]>>(
        '/api/privacycenter/tabconfiguration'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch tab configuration');
      }
      return data.result;
    },
  });
};

// DSR Type Options
export const useDsrTypeOptions = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.dsrTypeOptions,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<DsrTypeOption>>(
        '/api/privacycenter/dsrtypeoption'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch DSR type options');
      }
      return data.result;
    },
  });
};

// Consent Management
export const useConsentManagement = (email: string, enabled = true) => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.consentManagement(email),
    queryFn: async () => {
      const api = createApiClient(getHeaders);

      const [consentManagement, dataProcessingActivities] = await Promise.all([
        api.get<ApiResponse<any>>(
          '/api/privacycenter/consentmanagement',
          { params: { subjectEmail: email } }
        ),
        api.get<ApiResponse<any>>(
          '/api/privacycenter/dataprocessingactivities',
          { params: { subjectEmail: email } }
        ),
      ]);

      return {
        content: consentManagement.data.result,
        dataProcessingActivities: dataProcessingActivities.data.result,
      };
    },
    enabled: enabled && !!email,
  });
};

// DSR By Email
export const useDsrByEmail = (email: string, enabled = false) => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.dsrByEmail(email),
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.post<ApiResponse<DsrByEmailResponse>>(
        '/api/privacycenter/dsrbyemail',
        { email }
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch DSR by email');
      }
      return data.result;
    },
    enabled: enabled && !!email,
  });
};

// Countries
export const useCountries = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.countries,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<Country[]>>(
        '/api/privacycenter/countries'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch countries');
      }
      return data.result;
    },
  });
};

// States by Country
export const useStatesByCountry = (countryId: string, enabled = true) => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.states(countryId),
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<State[]>>(
        `/api/privacycenter/getstatesbycountry?countryId=${countryId}`
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch states');
      }
      return data.result;
    },
    enabled: enabled && !!countryId,
  });
};

// Country Calling Info
export const useCountryCallingInfo = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.countryCallingInfo,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<CountryCallingInfo[]>>(
        '/api/privacycenter/getcountriescallingcodes'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch calling codes');
      }
      return data.result;
    },
  });
};

// Inaccuracy Mapping
export const useInaccuracyMapping = () => {
  const { getHeaders } = useAuth();

  return useQuery({
    queryKey: queryKeys.inaccuracyMapping,
    queryFn: async () => {
      const api = createApiClient(getHeaders);
      const { data } = await api.get<ApiResponse<InaccuracyMapping>>(
        '/api/privacycenter/getsavedinaccuracymapping'
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Failed to fetch inaccuracy mapping');
      }
      return data.result;
    },
  });
};
